import { useRouter } from "next/router";
import React from "react";
import { getStripeCheckoutSession, getTicketsByEventSlug } from "../../../lib/ticketsRequests";
import { useToast } from "../../../context/toastContext";
import Loading from "../../global/Loading";
import Button from "../../global/Buttons/Button";
import { getStripe } from "../../../lib/stripeRequest";
import Link from "next/link";

export const Tickets = () => {
  const router = useRouter();
  const toast = useToast();
  const { slug } = router.query;
  const [loading, setLoading] = React.useState(true);
  const [event, setEvent] = React.useState({} as any);
  const [ticketTypes, setTicketTypes] = React.useState([]);
  const [selectedTickets, setSelectedTickets] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [tax, setTax] = React.useState(0);
  const [redirecting, setRedirecting] = React.useState(false);
  const [hasPastEvent, setHasPastEvent] = React.useState(false);
  const [errorCompletingPurchase, setErrorCompletingPurchase] = React.useState(false);

  React.useEffect(() => {
    const error = router.query.error;
    if (router.isReady && error) {
      setErrorCompletingPurchase(true);
    }
    
    if (router.isReady && slug?.length > 0) {
      const getQuantityText = (ticketType) => {
        if (
          ticketType.amount_available === 0 ||
          ticketType.remaining_tickets === 0
        ) {
          return "SOLD OUT";
        } else if (
          ticketType.allowed_per_person -
            ticketType.tickets_purchased_by_user <=
          0
        ) {
          return `Personal allotment of ${ticketType.allowed_per_person} tickets already purchased.`;
        }
        return "";
      };

      const fetchData = async () => {
        try {
          const { data } = await getTicketsByEventSlug(slug[0]);
          setEvent(data.event);
          setHasPastEvent(data.has_past);

          data.ticket_types = data.ticket_types.map((ticketType) => {
            return {
              ...ticketType,
              quantity_text: getQuantityText(ticketType),
              subtotal: 0,
            };
          });
          setTicketTypes(data.ticket_types);
        } catch (err) {
          toast("error", err.data?.error);
        } finally {
          setLoading(false);
        }
      };
      fetchData();
    }
  }, [slug, router.isReady]);

  React.useEffect(() => {
    const handlePageShow = () => {
      // Reset the loading state if the page is restored from bfcache
      setRedirecting(false); // This resets the loading state
    };

    window.addEventListener('pageshow', handlePageShow);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []);

  const itemPerTicketType = (ticket) => {
    const leftToPurchase = ticket.allowed_per_person - ticket.tickets_purchased_by_user;
    const quantityByTicketType = Math.min(leftToPurchase, ticket.allowed_per_person, ticket.remaining_tickets);

    return Array.from({ length: quantityByTicketType }, (_, i) => (
      <option key={i} value={i + 1}>
        {i + 1}
      </option>
    ));
  };

  const availableByUser = (ticket) => {
    const leftToPurchase = ticket.allowed_per_person - ticket.tickets_purchased_by_user;
    const quantityByTicketType = Math.min(leftToPurchase, ticket.allowed_per_person, ticket.remaining_tickets);
    return quantityByTicketType;
  };

  const handleOnClick = React.useCallback(async () => {
    try {
      setRedirecting(true);
      const { data } = await getStripeCheckoutSession({ticket_types: selectedTickets});
      const stripe = await getStripe();
      await stripe.redirectToCheckout({ sessionId: data.id });
      setRedirecting(false);
    } catch (err) {
      toast("error", err.data?.error);
      setRedirecting(false);
    }
  }, [selectedTickets, toast]);

  const handleSelectedTickets = React.useCallback(
    (ticketType, quantity) => {
      setTicketTypes((prev) => {
        const updatedTicketTypes = prev.map((ticket) => {
          if (ticket.id === ticketType.id) {
            ticket.subtotal =
              (ticket.price_in_cents / 100) * parseInt(quantity);
          }
          return ticket;
        });
        return updatedTicketTypes;
      });
      setSelectedTickets((prev) => {
        const quantityNumber = +quantity;
        const serviceFee = +ticketType.service_fee_percentage;
        if (quantityNumber === 0) {
          return prev.filter((item) => item.id !== ticketType.id);
        } else if (prev.find((item) => item.id === ticketType.id)) {
          return prev.map((item) => {
            if (item.id === ticketType.id) {
              return { ...item, quantity: quantityNumber, service_fee_percentage: serviceFee, item_per_user: availableByUser(ticketType) };
            }
            return item;
          });
        }
        return [...prev, { ...ticketType, quantity: quantityNumber, service_fee_percentage: serviceFee, item_per_user: availableByUser(ticketType) }];
      });
    },
    []
  );

  React.useEffect(() => {
    if (selectedTickets.length > 0) {
      let totalTaxes = 0;
      let totalAmount = 0;
      selectedTickets.forEach((ticket) => {
        const taxByTicket = ticket.service_fee_percentage / 100 * (ticket.price_in_cents / 100) * ticket.quantity;
        const subtotal = ticket.price_in_cents / 100 * ticket.quantity;
        totalTaxes += taxByTicket;
        totalAmount += subtotal + taxByTicket;
      })
      setTotal(totalAmount);
      setTax(totalTaxes);
    } else {
      setTotal(0);
      setTax(0);
    }
  }, [selectedTickets]);

  return (
    <>
      {loading ? (
        <Loading isLoading={loading} />
      ) : (
        hasPastEvent ? (
        <div className="bg-red w-full p-8 rounded flex items-center justify-center mb-4">
          <div className="text-center">
            <div className="text-2xl text-white">
              {event?.title} ticket sales have ended.
            </div>
            <div className="font-light text-white">
              We can&#39;t wait to see you at the next event!
            </div>
            <Button
              type="secondary"
              label="View Upcoming Events"
              className="mt-4 w-64 mx-auto bg-white text-red hover:shadow-lg"
              onClick={() => {
                router.push('/events');
              }}
            />
          </div>
        </div>
      ) :
        <div>
          { errorCompletingPurchase && 
          <div className="bg-red border border-red-400 px-4 py-3 rounded relative mb-6" role="alert">
            <p><strong className="font-bold text-white">We encountered an error processing your purchase.</strong></p>
            <span className="text-white">Please contact our support team for assitance at </span>
            <Link href="mailto:info@vinousmedia.com" className="cursor-pointer text-white hover:text-white hover:font-bold">info@vinous.com</Link>
          </div>
          }
          <h1 className="font-bold text-xl">
            Purchase your seats for {event.title}
          </h1>
          <div className="relative mt-4">
            <table className="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
              <thead className="text-xs text-gray-700 uppercase">
                <tr>
                  <th scope="col" className="px-6 py-3">
                    Ticket Type
                  </th>
                  <th scope="col" className="px-6 py-3">
                    Quantity
                  </th>
                  <th scope="col" className="px-6 py-3">
                    Price
                  </th>
                  <th scope="col" className="px-6 py-3">
                    Subtotal
                  </th>
                </tr>
              </thead>
              <tbody>
                {ticketTypes
                  .map((ticketType) => (
                    <tr className="bg-white border-b" key={ticketType.id}>
                      <th
                        scope="row"
                        style={{ maxWidth: "400px" }}
                        className="px-6 py-4 font-medium text-gray-900"
                      >
                        {" "}
                        {ticketType.name}{" "}
                      </th>
                      <td className="px-6 py-4 w-36">
                        {ticketType.quantity_text.length > 0 ? (
                          <p>{ticketType.quantity_text}</p>
                        ) : (
                          <select
                            id="ticketTypes"
                            className="bg-gray-50 border border-gray-300 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 text-black"
                            onChange={(e) => {
                              handleSelectedTickets(
                                ticketType,
                                e.currentTarget.value
                              );
                            }}
                          >
                            <option defaultValue={0}>0</option>
                            {itemPerTicketType(ticketType)}
                          </select>
                        )}
                      </td>
                      <td className="px-6 py-4 text-black">
                        ${(ticketType.price_in_cents / 100).toFixed(2)}
                      </td>
                      {ticketType.subtotal > 0 && (
                        <td className="px-6 py-4 text-black">
                          ${ticketType.subtotal.toFixed(2)}
                        </td>
                      )}
                    </tr>
                  ))}
              </tbody>
            </table>
            <div className="flex justify-end m-6">
              <div className="xs:w-1/2 md:w-1/4">
                <div className="flex justify-between mb-4">
                  <span>Tax/Service Fees:</span>
                  <span>${tax.toFixed(2)}</span>
                </div>
                <div className="flex justify-between mb-4">
                  <span className="font-bold text-base">Total:</span>
                  <span> ${total.toFixed(2)}</span>
                </div>
              </div>
            </div>

            <div className="flex xs:justify-between md:justify-end lg:justify-between">
              <p>Please note all sales are final. Unfortunately we can’t accept exchanges, refunds or cancellations.
            For more information email: <Link href="mailto: events@vinous.com">events@vinous.com</Link></p>
              <Button
                onClick={handleOnClick}
                type="Update"
                label="Checkout"
                className="w-1/3"
                disabled={selectedTickets.length === 0 || redirecting}
                isLoading={redirecting}
              ></Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
